/**
 * @prettier
 */

import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import cx from 'classnames';
import { CheckboxField } from '@zola/zola-ui/src/components/Form/CheckboxField';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { InputFieldV2 } from '@zola/zola-ui/src/components/Form/InputFieldV2';
import { DatePickerInput } from '@zola/zola-ui/src/components/Form/DatePickerInput';
import { DropdownV2 } from '@zola/zola-ui/src/components/Form/Dropdown';
import styles from './styles.module.less';

const Header = ({ children }) => {
  return <div className={styles.header}>{children}</div>;
};

const Text = ({ children }) => {
  return <div className={styles.text}>{children}</div>;
};

const Spacer = ({ hideOnMobile }) => {
  return <div className={cx(styles.spacer, { [styles.hideOnMobile]: hideOnMobile })} />;
};

const InputGroup = ({ className, columnOnMobile, children }) => {
  return (
    <div className={cx(className, styles.inputGroup, { [styles.columnOnMobile]: columnOnMobile })}>
      {children}
    </div>
  );
};

const Field = ({ className, small, ...props }) => {
  return (
    <div className={cx(className, styles.field, { [styles.small]: small })}>
      <InputFieldV2 {...props} />
    </div>
  );
};

// Date field helpers
const formatDate = date => moment.max(moment(date), moment()).format('YYYY-MM-DD');

const parseDate = dateString => {
  // Return today's date if the input is empty or invalid
  if (!dateString || dateString.length === 0 || !moment(dateString).isValid()) {
    return moment().toDate();
  }
  // Fallback on today's date if selected date is in the past
  return moment.max(moment(dateString), moment()).toDate();
};

const DateField = ({ className, label, inputName, disabled, value, onChange }) => {
  const [inputValue, setInputValue] = useState(value);
  const isProperlyFormatted =
    inputValue === moment(inputValue).format('YYYY-MM-DD') || (!inputValue && inputValue === value);

  // Only save the new value to the form if correctly formatted and has changed
  useEffect(() => {
    if (isProperlyFormatted && inputValue !== value) {
      onChange(inputValue);
    }
  }, [onChange, value, inputValue, isProperlyFormatted]);

  return (
    <DatePickerInput
      className={cx(className, styles.field)}
      label={label}
      disabled={disabled}
      formatDate={formatDate}
      parseDate={parseDate}
      isFormField
      inputOnChange={e => setInputValue(e.target.value)}
      value={inputValue || ''}
      inputFieldProps={{
        name: inputName,
        errorMessage: isProperlyFormatted ? '' : 'Oops! Please enter a valid date',
      }}
    />
  );
};

const Checkbox = ({ className, id, disabled, label, checked, onChange }) => {
  return (
    <CheckboxField
      className={cx(styles.checkbox, className)}
      id={id}
      label={label}
      disabled={disabled}
      checked={checked}
      onChange={e => onChange(e.target.checked)}
    />
  );
};

const Dropdown = ({
  className,
  isRequired,
  name,
  label,
  options,
  selected,
  onSelect,
  errorMessage,
}) => {
  return (
    <div className={cx(className, styles.field)}>
      <DropdownV2
        label={label}
        isRequired={isRequired}
        onSelect={onSelect}
        options={options}
        value={selected}
        id={name}
        className={styles.defensiveDropdownFix}
        errorMessage={errorMessage}
      />
    </div>
  );
};

const FormButtons = ({
  className,
  backLabel,
  nextLabel,
  nextDisabled,
  backDisabled,
  backClassName,
  nextClassName,
  onBack,
  onNext,
}) => {
  return (
    <div className={cx(styles.formButtonBox, className)}>
      <ButtonV2
        variant="secondary"
        className={cx(styles.backButton, backClassName)}
        disabled={backDisabled}
        onClick={onBack}
      >
        {backLabel}
      </ButtonV2>
      <Spacer />
      <ButtonV2
        type="submit"
        variant="primary"
        className={cx(styles.nextButton, nextClassName)}
        disabled={nextDisabled}
        onClick={onNext}
      >
        {nextLabel}
      </ButtonV2>
    </div>
  );
};

const Form = ({ onSubmit, children }) => {
  const submit = useCallback(
    event => {
      event.preventDefault();
      onSubmit(event);
    },
    [onSubmit]
  );

  return <form onSubmit={submit}>{children}</form>;
};

const PreviewBox = ({ children }) => {
  return <div className={styles.previewBox}>{children}</div>;
};

const Row = ({ children }) => {
  return <div className={styles.row}>{children}</div>;
};

const Col = ({ children }) => {
  return <div className={styles.col}>{children}</div>;
};

const Box = ({ children }) => {
  return <div className={styles.box}>{children}</div>;
};

export default {
  Header,
  Text,
  Spacer,
  InputGroup,
  Field,
  DateField,
  Checkbox,
  Dropdown,
  FormButtons,
  Form,
  PreviewBox,
  Row,
  Col,
  Box,
};
